import { LegalShell, LegalSection, SupportLink } from "../site/LegalShell.jsx";

// Deliberately free of commitments. Everything forward-looking is written as
// intent ("we want to", "we intend to") rather than a pledge, there are no
// numbers attached to the giving-back section, and no claim is made about
// being cheapest or having rates nobody else can get — those are the lines
// that turn a friendly about page into a liability.
export default function AboutPage() {
  return (
    <LegalShell
      eyebrow="Our story"
      title="We only do Hawaiʻi."
      intro="Aloha Rates is a small travel company built around one set of islands. We started it because we love this place, and because booking a trip here shouldn't feel like wrestling a search engine built for everywhere else."
    >
      <LegalSection heading="Why we built it">
        <p>
          Most booking sites treat Hawaiʻi as one more entry in a dropdown of thousands of destinations.
          Search from one of those and you get generic results, prices that grow at the last step, and
          photos of a lobby that could be anywhere on earth.
        </p>
        <p>
          We wanted somewhere that knows the difference between the North Shore in January and the North
          Shore in July. Somewhere that puts the condo down a side street in Kīhei next to the resort on the
          beach, because plenty of the time the condo is the better trip. So we built it, for six islands
          and nothing else.
        </p>
      </LegalSection>

      <LegalSection heading="How we actually work">
        <p>
          We are an online travel agency. We don't own the hotels or the rentals. We work with supply
          partners to show what's available across the islands and let you book it in a few taps, and rates
          move constantly, so what you see is what the property is asking at the moment you look.
        </p>
        <p>
          Where we can, we go further than reselling. We negotiate rates directly with properties through our
          supply partner, which means some of what you'll find here isn't on the big booking sites at all.
          Being small and Hawaiʻi-only is what makes that conversation possible.
        </p>
        <p>
          Two things we hold ourselves to. Every price we show is a total with taxes and fees included, and
          anything the property collects at check-in is called out separately before you pay rather than
          discovered at the front desk. And where a rate comes with free cancellation, we say so on the room
          itself instead of burying it three screens deep.
        </p>
      </LegalSection>

      <LegalSection heading="Run a place in Hawaiʻi?">
        <p>
          We're opening up direct listings, and we'd rather work with owners and managers who know their
          corner of the islands than fill the site with everything that exists.{" "}
          <a href="/list-your-property/" className="text-ocean-700 underline underline-offset-2 hover:text-ocean-900">
            Tell us about your property
          </a>{" "}
          and we'll get in touch.
        </p>
      </LegalSection>

      <LegalSection heading="Giving back">
        <p>
          We're young, so we would rather tell you what we hope to do than dress it up as a program. As
          Aloha Rates grows, we want to put part of what we earn back into the places we send travelers to.
          Beach and reef cleanups, local food banks, the groups doing the unglamorous work of keeping these
          islands liveable for the people who actually live here.
        </p>
        <p>
          We're not going to attach a number or a logo to that until it's real. When it is, you'll read
          about it here first.
        </p>
      </LegalSection>

      <LegalSection heading="What we won't tell you">
        <p>
          We won't claim to be the cheapest on every stay, because nobody is and you'd catch us out.
          We won't invent scarcity or run a countdown clock to hurry you into booking. And we won't
          advertise a partnership or a donation we haven't actually made.
        </p>
        <p>
          If we get something wrong, a price, a photo, a closed property, or a Hawaiian place name missing
          its ʻokina or kahakō, tell us and we'll fix it. Email <SupportLink/> and a person will read it.
        </p>
      </LegalSection>

      <div className="card p-8">
        <p className="text-xs uppercase tracking-[0.14em] text-ink-muted">Start somewhere</p>
        <p className="mt-3 font-display text-2xl md:text-3xl leading-tight text-ink">
          Pick an island and see what's open.
        </p>
        <p className="prose-body mt-4">
          Or browse everything at once if you're still deciding. Mahalo for reading this far.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <a href="/results/" className="btn-primary">Search stays</a>
          <a href="/edit/" className="btn-secondary">Read the guides</a>
        </div>
      </div>
    </LegalShell>
  );
}
